import { z } from 'zod'

import type {
  CommonResponseLessonClassDetailResponse,
  CommonResponseListAssignedTeacherListResponse,
  CommonResponseSliceLessonClassListWithMemberResponseStudentMemberResponse,
  CommonResponseSliceLessonClassResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { mapSliceContent, type Slice, sliceSchema } from '~/schema/slice'

import {
  type LessonClass,
  type LessonClassAttendanceGroup,
  LessonClassAttendanceGroupSchema,
  type LessonClassDetail,
  LessonClassDetailSchema,
  type LessonClassPhoneGroup,
  LessonClassPhoneGroupSchema,
  LessonClassSchema,
  type LessonClassTeacher,
  LessonClassTeacherSchema,
} from './schemas'

const ENTITY = 'LessonClass'

// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 lessonClass/controller/LessonClassController.kt·LessonClassPresenter.kt·
// response/ 와 student/controller/response/StudentMemberResponse.kt 로 확인.

// 생성 타입은 이름 예시를 optional 로 그리지만 서버는 키를 빼지 않고 null 을 싣는다(LessonClassResponse 의 String?).
const asWire = (raw: CommonResponseSliceLessonClassResponse['data']): unknown => raw

export const adaptLessonClasses = (
  raw: CommonResponseSliceLessonClassResponse['data'],
): Slice<LessonClass> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(LessonClassSchema), asWire(raw), ENTITY),
    (lessonClass) => lessonClass,
  )

export const adaptLessonClassDetail = (
  raw: CommonResponseLessonClassDetailResponse['data'],
): LessonClassDetail => parseApiResponse(LessonClassDetailSchema, raw, ENTITY)

export const adaptLessonClassTeachers = (
  raw: CommonResponseListAssignedTeacherListResponse['data'],
): LessonClassTeacher[] => parseApiResponse(z.array(LessonClassTeacherSchema), raw, ENTITY)

// 생성 타입은 멤버를 공통 베이스(id·name)로만 그린다. 실제로는 엔드포인트마다 하위 클래스
// (StudentMemberWithAttendanceResponse·StudentMemberWithPhoneNumberResponse)가 직렬화되어 그 필드가 함께 온다.
const asGroupWire = (
  raw: CommonResponseSliceLessonClassListWithMemberResponseStudentMemberResponse['data'],
): unknown => raw

export const adaptLessonClassAttendanceGroups = (
  raw: CommonResponseSliceLessonClassListWithMemberResponseStudentMemberResponse['data'],
): Slice<LessonClassAttendanceGroup> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(LessonClassAttendanceGroupSchema), asGroupWire(raw), ENTITY),
    (group) => group,
  )

export const adaptLessonClassPhoneGroups = (
  raw: CommonResponseSliceLessonClassListWithMemberResponseStudentMemberResponse['data'],
): Slice<LessonClassPhoneGroup> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(LessonClassPhoneGroupSchema), asGroupWire(raw), ENTITY),
    (group) => group,
  )
